import { useEffect, useState } from 'react'
import { Loader2, Pencil, Stamp } from 'lucide-react'
import { useQuoteItemApi } from 'src/api/db/apiQuoteItem'
import { useQuoteSubItemApi } from 'src/api/db/apiQuoteSubItem'
import { useGetDecorationInfo } from 'src/api/server/useGetDecorationInfo'
import { Column } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/shared/components/ui/dialog'
import { DropdownMenuItem } from 'src/shared/components/ui/dropdown-menu'
import { useToast } from 'src/shared/hooks/use-toast'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { DecorationPicker } from './DecorationPicker'

export const ChangeDecorationMenuItems = ({
  onManualDesign,
  onChangeDecoration,
}: {
  onManualDesign: () => void
  onChangeDecoration: () => void
}) => {
  const { t } = useLocalization()
  return (
    <>
      <DropdownMenuItem className="gap-2" onClick={onManualDesign}>
        <Pencil className="h-3.5 w-3.5" />
        {t('Manual design', 'Manuell design')}
      </DropdownMenuItem>
      <DropdownMenuItem className="gap-2" onClick={onChangeDecoration}>
        <Stamp className="h-3.5 w-3.5" />
        {t('Change decoration', 'Byt dekoration')}
      </DropdownMenuItem>
    </>
  )
}

export const ChangeDecorationDialog = ({
  open,
  onOpenChange,
  quoteItemId,
  quoteSubItemId,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  quoteItemId: number
  quoteSubItemId: number
}) => {
  const { t } = useLocalization()
  const { toast } = useToast()
  const { quoteItem } = useQuoteItemApi({
    quote_item_id: open ? quoteItemId : undefined,
  })
  const { updateQuoteSubItem, deleteQuoteSubItem, createQuoteSubItem } =
    useQuoteSubItemApi()

  const quote_item = quoteItem?.data
  const quote_sub_item = quote_item?.quote_sub_item?.find(
    (subItem) => subItem.id === quoteSubItemId
  )
  const productId = quote_item?.product?.id

  const { decorations, isPending: decorationsPending } = useGetDecorationInfo({
    product_id: open && productId ? productId : 0,
    item_quantity: quote_sub_item?.quantity ?? 1,
    item_colors: quote_sub_item?.d_colors ?? 1,
  })

  const [printCode, setPrintCode] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (open) {
      setPrintCode(quote_sub_item?.print_code ?? null)
    }
  }, [open, quote_sub_item?.print_code])

  const isLoading = quoteItem?.isPending || decorationsPending
  const hasChanged = printCode !== (quote_sub_item?.print_code ?? null)

  const handleSave = async () => {
    if (!quote_item || !quote_sub_item || !printCode || !hasChanged) return

    const chosenDecoration = decorations?.find(
      (d: { print_code: string }) => d.print_code === printCode
    )
    if (!chosenDecoration) return

    setIsSaving(true)
    try {
      const existingSetupCost = quote_item.quote_sub_item?.find(
        (qsi) => qsi.type === 'setup_cost' && qsi.quote_item_id === quote_item.id
      )
      if (existingSetupCost) {
        await deleteQuoteSubItem.mutateAsync(existingSetupCost.id)
      }

      await updateQuoteSubItem.mutateAsync({
        id: quote_sub_item.id,
        print_code: printCode,
        description: chosenDecoration.info?.name || '-',
        unit_price: Math.round(chosenDecoration.unit_cost ?? 0),
      })

      if (chosenDecoration.setup_cost && chosenDecoration.setup_cost > 0) {
        await createQuoteSubItem.mutateAsync({
          quote_item_id: quote_item.id,
          type: 'setup_cost',
          unit_price: chosenDecoration.setup_cost,
          quantity: 1,
          description: 'Setup cost',
        })
      }

      toast({
        title: t('Decoration updated', 'Dekoration uppdaterad'),
        variant: 'success',
      })
      onOpenChange(false)
    } catch (error) {
      console.error('Failed to change decoration', error)
      toast({
        title: t('Something went wrong', 'Något gick fel'),
        description: t(
          'Could not update the decoration.',
          'Kunde inte uppdatera dekorationen.'
        ),
        variant: 'destructive',
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md gap-4">
        <DialogHeader>
          <DialogTitle>
            {t('Change decoration', 'Byt dekoration')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'Choose a print technique. Artwork and placement stay as they are.',
              'Välj en tryckteknik. Motiv och placering behålls.'
            )}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <Column className="items-center justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </Column>
        ) : (
          <Column className="max-h-[min(60vh,480px)] overflow-y-auto">
            <DecorationPicker
              quoteSubItem={quote_sub_item}
              selectedPrintCode={printCode}
              setPrintCode={(val: { print_code: string | null }) =>
                setPrintCode(val.print_code)
              }
              decorations={decorations}
              withTitle={false}
              withCard={false}
              fullHeight
            />
          </Column>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            {t('Cancel', 'Avbryt')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={isLoading || isSaving || !printCode || !hasChanged}
          >
            {isSaving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            {t('Save', 'Spara')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
